import { useState, useEffect } from "react";
import { toast } from "sonner";
import { productsService } from "../../services/products";
import { authService } from "../../services/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox"; // Added Checkbox import
import { collectionsService } from "../../services/collections";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MultiSelect } from "@/components/ui/multi-select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Plus, Pencil, Trash2, Package, Check, ChevronsUpDown, X, Upload, Info, DollarSign, Image as ImageIcon, Settings, Layers, Star, ArrowLeft, Search, Sliders } from "lucide-react";
import ChannelOverrideDialog from "../../components/admin/ChannelOverrideDialog";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";
import { customizationsService } from "../../services/customizations";
import { getImageUrl } from "../../utils/imageUtils";

const CHANNEL_LABEL = { edisplay: "ED", funeral: "FS", infinity: "IT" };
const CHANNEL_COLOR = {
  edisplay: "border-orange-200 bg-orange-50 text-orange-700",
  funeral: "border-rose-200 bg-rose-50 text-rose-700",
  infinity: "border-blue-200 bg-blue-50 text-blue-700",
};

// Full names for the "Available In" picker — the two-letter CHANNEL_LABEL above is
// for the dense per-row badges, and would be unreadable as a form option.
const CHANNEL_OPTION_LABEL = {
  edisplay: "e-Displayroom",
  funeral: "Funeral Supplies",
  infinity: "Infinity Touch",
};

/**
 * Turn the picker's channel list into the `channel_overrides` the API stores.
 *
 * Explicit 'include'/'exclude' on every channel rather than leaving unpicked ones
 * null: null means "inherit from collections", so a product deselected here would
 * quietly come back through a collection tag. The admin picked a list; that list
 * is the answer.
 */
const channelsToOverrides = (channels, offered) => {
  const chosen = new Set(channels || []);
  return offered.reduce((acc, channel) => {
    acc[channel] = chosen.has(channel) ? 'include' : 'exclude';
    return acc;
  }, {});
};

/** Channels a product currently sells on, for hydrating the form when editing. */
const channelsFromProduct = (product) =>
  Array.isArray(product?.effective_channels) ? product.effective_channels : [];

export default function AdminProducts({ includeFuneralChannel = false, enableChannelColumn = false } = {}) {
  const [products, setProducts] = useState([]);
  // Product Management → Products page opens the per-product channel popup on this row.
  const [channelDialogProduct, setChannelDialogProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [allCustomizations, setAllCustomizations] = useState([]);
  const [availableCollections, setAvailableCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterCollection, setFilterCollection] = useState([]);
  const [filterAddon, setFilterAddon] = useState([]);
  const [filterSource, setFilterSource] = useState("admin");
  const [filterChannel, setFilterChannel] = useState("all"); // all (eD+IT) | edisplay | infinity

  // The channels this page is allowed to offer, matching the scope it lists:
  // Platform → Products is eD + IT; Product Management → Products adds Funeral.
  const offeredChannels = includeFuneralChannel
    ? ["edisplay", "funeral", "infinity"]
    : ["edisplay", "infinity"];
  const channelOptions = offeredChannels.map((c) => ({ label: CHANNEL_OPTION_LABEL[c], value: c }));
  // A new product defaults to whatever the list is currently narrowed to, so
  // "filter to Infinity Touch → Add Product" produces an Infinity product.
  const defaultChannels = filterChannel !== "all" ? [filterChannel] : offeredChannels;
  const [searchKeyword, setSearchKeyword] = useState("");
  const [appliedKeyword, setAppliedKeyword] = useState("");
  const [parentProductBeforeVariant, setParentProductBeforeVariant] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(15);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const [parentProductName, setParentProductName] = useState(null); // To track if we are creating a variant
  const [activeTab, setActiveTab] = useState("general");
  const [editingProductVariants, setEditingProductVariants] = useState([]); // To store variants for the currently edited product

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    base_price: "",
    category_id: "",
    image_url: "", // Keep for backward compatibility or single URL entry
    images: [],
    sku: "",
    inventory_quantity: 0,
    customizations: { mandatory: [], optional: [] },
    is_addon: false,
    addons: [],
    collections: [],
    is_variant: false,
    parent_id: null,
    variant_name: "",
    overridden_price: "",
    infinity_overridden_price: "",
    is_active: true,
    is_available: true,
    // Channels this product sells on. Persisted as `channel_overrides`, not as a
    // channels array — see channelsToOverrides below.
    channels: [],
  });

  useEffect(() => {
    fetchData();
  }, [page, limit, filterCategory, filterCollection, filterAddon, filterSource, filterChannel, appliedKeyword]); // Refetch on changes

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch categories (available to all)
      const categoriesData = await productsService.categories.getAll();
      setCategories(categoriesData);

      // Fetch Customizations
      const customizationsData = await customizationsService.getAll();
      setAllCustomizations(customizationsData.filter(c => c.is_active).map(c => ({
        label: `${c.name} ($${c.price})`,
        value: c.customization_id
      })));

      // Fetch Collections (Show all including inactive in admin)
      const collectionsResponse = await collectionsService.getAll(false);
      const collectionsData = collectionsResponse.data || collectionsResponse;
      setAvailableCollections(collectionsData.map(c => ({
        label: c.name,
        value: c._id, // Using _id as reference in Product model
        is_active: c.is_active
      })));

      // Fetch products (Unified endpoint handles role-based return)
      const params = { page, limit, only_parents: true };
      if (filterCategory.length > 0) {
        params.category_id = filterCategory.join(",");
      }
      if (filterCollection.length > 0) {
        params.collection_id = filterCollection.join(",");
      }
      if (filterAddon.length === 1) {
        params.is_addon = filterAddon[0] === "only_addons";
      }
      if (filterSource !== "all") {
        params.source = filterSource;
      }
      // Platform → Products shows only Edisplayroom + Infinity Touch products
      // (excludes funeral-only). The unified Product Management → Products page
      // sets includeFuneralChannel to also surface funeral products.
      // Channel dropdown narrows to a single channel.
      if (filterChannel !== "all") {
        params.channels = filterChannel;
      } else {
        params.channels = includeFuneralChannel ? "edisplay,funeral,infinity" : "edisplay,infinity";
      }
      if (appliedKeyword) {
        params.keyword = appliedKeyword;
      }
      // If length === 2 (both selected) or length === 0 (none selected), we don't apply the is_addon filter.
      const productsData = await productsService.adminProducts.getAll(params);

      // Force recalculation of pages if we lost sync
      if (productsData.data && productsData.pagination) {
        setProducts(productsData.data);
        const calcTotalPages = Math.ceil(productsData.pagination.total / limit) || 1;
        setTotalPages(calcTotalPages);
        setTotalItems(productsData.pagination.total);
        if (page > calcTotalPages && calcTotalPages > 0) setPage(1);
      } else {
        // Fallback for arrays
        const rawProducts = productsData.data || productsData;

        // As fallback, we do manual client side filtering and pagination if the backend somehow ignores `only_parents`
        // We will trust backend for now, but client side fallback is safe
        const parents = rawProducts.filter(p => !p.parent_id);
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;

        setProducts(parents.slice(startIndex, endIndex));
        const fallbackTotalPages = Math.ceil(parents.length / limit) || 1;
        setTotalPages(fallbackTotalPages);
        setTotalItems(parents.length);
        if (page > fallbackTotalPages && fallbackTotalPages > 0) setPage(1);
      }

    } catch (error) {
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name?.trim()) {
      return toast.error("Product name is required.");
    }
    if (!formData.base_price) {
      return toast.error("Base price is required.");
    }
    if (formData.overridden_price && parseFloat(formData.overridden_price) < parseFloat(formData.base_price)) {
      return toast.error("Override Price cannot be less than Base Price.");
    }

    try {
      let finalCategoryId = formData.category_id;

      // Auto-assign "General" category if not selected and not a variant
      if (!formData.is_variant && !finalCategoryId) {
        const generalCategory = categories.find(c => c.name.toLowerCase() === "general");
        if (generalCategory) {
          finalCategoryId = generalCategory.category_id;
        } else {
          return toast.error("Category is required and a default 'General' category could not be found.");
        }
      }

      // A product with no channel is invisible everywhere — the exact defect this
      // picker exists to prevent — so refuse to create one silently.
      if (!formData.is_variant && (formData.channels || []).length === 0) {
        return toast.error("Pick at least one channel under \"Available In\", or the product will not appear in any store.");
      }

      // `channels` is the picker's own state; the product stores channel_overrides.
      const { channels: pickedChannels, ...productFields } = formData;

      const data = {
        ...productFields,
        category_id: finalCategoryId,
        // Variants inherit their parent's channels; only parents carry overrides.
        ...(formData.is_variant
          ? {}
          : { channel_overrides: channelsToOverrides(pickedChannels, offeredChannels) }),
        base_price: parseFloat(formData.base_price),
        overridden_price: formData.overridden_price ? parseFloat(formData.overridden_price) : 0,
        infinity_overridden_price: formData.infinity_overridden_price ? parseFloat(formData.infinity_overridden_price) : 0,
        inventory_quantity: parseInt(formData.inventory_quantity)
      };

      if (editingProduct) {
        await productsService.adminProducts.update(editingProduct.product_id, data);
        toast.success("Product updated successfully");
      } else {
        await productsService.adminProducts.create(data);
        toast.success(formData.is_variant ? "Variant created successfully" : "Product created successfully");
      }

      if (formData.is_variant && formData.parent_id) {
        if (parentProductBeforeVariant) {
          const parent = parentProductBeforeVariant;
          setParentProductBeforeVariant(null);
          handleEdit(parent);
          setTimeout(() => setActiveTab("variants"), 0);
        } else {
          fetchEditingProductVariants(formData.parent_id);
          setParentProductName(null);
          resetForm();
          setActiveTab("variants"); // Keep them on variants tab to see the newly created one
        }
      } else {
        setDialogOpen(false);
        setEditingProduct(null);
        setParentProductName(null);
        resetForm();
        fetchData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save product");
    }
  };

  const fetchEditingProductVariants = async (parentId) => {
    try {
      // Use the standard search endpoint to find variants of this parent
      const response = await productsService.adminProducts.getAll({ parent_id: parentId, limit: 100 });
      const variantData = response.data?.data || response.data || response;
      if (Array.isArray(variantData)) {
        setEditingProductVariants(variantData);
      }
    } catch (error) {
      console.error("Failed to fetch variants:", error);
      toast.error("Could not load product variants.");
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setParentProductName(null); // Reset this as we are editing an existing item
    if (!product.is_variant && !product.is_addon) {
      fetchEditingProductVariants(product.product_id);
    }
    setFormData({
      name: product.name,
      description: product.description || "",
      base_price: product.base_price.toString(),
      category_id: product.category_id,
      image_url: product.image_url || "",
      images: product.images || [],
      sku: product.sku || "",
      inventory_quantity: product.inventory_quantity,
      customizations: product.customizations || { mandatory: [], optional: [] },
      is_addon: product.is_addon || false,
      addons: product.addons || [],
      collections: product.collections || [],
      is_variant: product.is_variant || false,
      parent_id: product.parent_id || null,
      variant_name: product.variant_name || "",
      overridden_price: product.overridden_price ? product.overridden_price.toString() : "",
      infinity_overridden_price: product.infinity_overridden_price ? product.infinity_overridden_price.toString() : "",
      is_active: product.is_active !== undefined ? product.is_active : true,
      is_available: product.is_available !== undefined ? product.is_available : true,
      // Show only the channels this page offers, so editing from Platform → Products
      // cannot silently drop a Funeral assignment it never displayed.
      channels: channelsFromProduct(product).filter((c) => offeredChannels.includes(c)),
    });
    setDialogOpen(true);
    setTimeout(() => setActiveTab("general"), 0);
  };

  const handleDelete = async (productId, isRemovingVariant = false, parentId = null) => {
    if (!window.confirm("Are you sure you want to delete this product?")) return;

    try {
      await productsService.adminProducts.delete(productId);
      toast.success(isRemovingVariant ? "Variant deleted successfully" : "Product deleted successfully");

      if (isRemovingVariant && parentId) {
        fetchEditingProductVariants(parentId);
      } else {
        fetchData();
      }
    } catch (error) {
      toast.error("Failed to delete product");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      base_price: "",
      category_id: "",
      image_url: "",
      images: [],
      sku: "",
      inventory_quantity: 0,
      customizations: { mandatory: [], optional: [] },
      is_addon: false,
      addons: [],
      collections: [],
      is_variant: false,
      parent_id: null,
      variant_name: "",
      overridden_price: "",
      // Was missing, so an Infinity price override leaked from the last edited
      // product into the "Add Product" form (the submit spreads all of formData).
      infinity_overridden_price: "",
      is_active: true,
      is_available: true,
      channels: defaultChannels,
      });
    setParentProductName(null);
    setParentProductBeforeVariant(null);
    setEditingProductVariants([]);
    setActiveTab("general");
  };

  const openNewDialog = () => {
    setEditingProduct(null);
    resetForm();
    setDialogOpen(true);
    setActiveTab("general");
  };

  const getCategoryName = (categoryId) => {
    const category = categories.find(c => c.category_id === categoryId);
    return category?.name || "Unknown";
  };

  // Filter is handled by backend now, but we keep this for local safe-guarding or unpaginated arrays
  const filteredProducts = products;

  // Helper to format collections into a badge list
  const getCollectionNames = (collectionIds) => {
    if (!collectionIds || collectionIds.length === 0) return <span className="text-slate-400 text-xs italic">Unassigned</span>;
    return (
      <div className="flex flex-wrap gap-1">
        {collectionIds.map(id => {
          const coll = availableCollections.find(c => c.value === id);
          if (!coll) {
            return <Badge key={id} variant="secondary" className="text-[10px] bg-slate-50 text-slate-400 border-slate-200">Unknown</Badge>;
          }
          return (
            <Badge 
              key={id} 
              variant={coll.is_active ? "secondary" : "outline"} 
              className={cn(
                "text-[10px]",
                !coll.is_active && "text-amber-600 border-amber-200 bg-amber-50"
              )}
            >
              {coll.label} {!coll.is_active && "(Hidden)"}
            </Badge>
          );
        })}
      </div>
    );
  };

  // Filter available add-ons: Must be active and marked as is_addon
  const availableAddons = products
    .filter(p => p.is_addon && p.product_id !== editingProduct?.product_id) // Avoid self-reference if editing
    .map(p => ({
      label: `${p.name} ($${p.base_price})`,
      value: p.product_id
    }));

  // We removal the full-page loading check here so that the layout remains visible
  // The loading indicator will be handled inside the table body or as an overlay

  return (
    <div className="space-y-6 animate-fade-in" data-testid="admin-products">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">
            Products
          </h2>
          <p className="text-slate-500">Manage your product catalog</p>
        </div>
        <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search products..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setAppliedKeyword(searchKeyword);
                  setPage(1);
                }
              }}
              className="pl-10 h-10"
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            className="h-10 px-3"
            onClick={() => { setAppliedKeyword(searchKeyword); setPage(1); }}
          >
            <Search className="w-4 h-4" />
          </Button>
          {appliedKeyword && (
            <Button
              variant="ghost"
              size="sm"
              className="h-10 px-2 text-slate-500"
              onClick={() => { setSearchKeyword(""); setAppliedKeyword(""); setPage(1); }}
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
          )}
          <div className="w-56">
            <MultiSelect
              options={availableCollections}
              selected={filterCollection}
              onChange={(val) => { setFilterCollection(val); setPage(1); }}
              placeholder="Filter by collection"
            />
          </div>

          <div className="w-56">
            <MultiSelect
              options={[
                { label: "Standard Products", value: "standard" },
                { label: "Add-ons Only", value: "only_addons" }
              ]}
              selected={filterAddon}
              onChange={(val) => { setFilterAddon(val); setPage(1); }}
              placeholder="Filter by type"
            />
          </div>

          <div className="w-48">
            <Select value={filterChannel} onValueChange={(val) => { setFilterChannel(val); setPage(1); }}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Filter by channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{includeFuneralChannel ? "Channel: All" : "Channel: All ED + IT"}</SelectItem>
                <SelectItem value="edisplay">Channel: e-Displayroom</SelectItem>
                {includeFuneralChannel && <SelectItem value="funeral">Channel: Funeral Supplies</SelectItem>}
                <SelectItem value="infinity">Channel: Infinity Touch</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
            <SheetTrigger asChild>
              <Button
                onClick={openNewDialog}
                className="bg-primary hover:bg-primary/90 rounded-xl"
                data-testid="add-product-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Product
              </Button>
            </SheetTrigger>
            <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
              <SheetHeader>
                <div className="flex items-center gap-2">
                  {formData.is_variant && parentProductBeforeVariant && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const parent = parentProductBeforeVariant;
                        setParentProductBeforeVariant(null);
                        handleEdit(parent);
                        setTimeout(() => setActiveTab("variants"), 0);
                      }}
                      className="mr-2"
                    >
                      <ArrowLeft className="w-4 h-4 mr-1" />
                      Back to Parent
                    </Button>
                  )}
                  <SheetTitle>
                    {editingProduct
                      ? (formData.is_variant ? "Edit Variant" : "Edit Product")
                      : formData.is_variant
                        ? `Add Variant for ${parentProductName}`
                        : "Add New Product"}
                  </SheetTitle>
                </div>
              </SheetHeader>

              <form onSubmit={handleSubmit} className="space-y-4 mt-6">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className={cn("grid w-full", (editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) ? "grid-cols-5" : "grid-cols-4")}>
                    <TabsTrigger value="general">
                      <Info className="w-4 h-4 mr-2 hidden sm:inline" />
                      General
                    </TabsTrigger>
                    <TabsTrigger value="pricing">
                      <DollarSign className="w-4 h-4 mr-2 hidden sm:inline" />
                      Pricing
                    </TabsTrigger>
                    <TabsTrigger value="images">
                      <ImageIcon className="w-4 h-4 mr-2 hidden sm:inline" />
                      Images
                    </TabsTrigger>
                    <TabsTrigger value="custom">
                      <Settings className="w-4 h-4 mr-2 hidden sm:inline" />
                      Custom
                    </TabsTrigger>
                    {(editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) && (
                      <TabsTrigger value="variants">
                        <Layers className="w-4 h-4 mr-2 hidden sm:inline" />
                        Variants
                      </TabsTrigger>
                    )}
                  </TabsList>

                  <TabsContent value="general" className="space-y-4 mt-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {formData.is_variant && (
                        <div className="space-y-2">
                          <Label htmlFor="variant_name">Variant Name / Option <span className="text-red-500">*</span></Label>
                          <Input
                            id="variant_name"
                            required
                            value={formData.variant_name}
                            onChange={(e) => setFormData(prev => ({ ...prev, variant_name: e.target.value }))}
                            className="h-12 border-indigo-200 focus-visible:ring-indigo-500"
                            data-testid="product-variant-name-input"
                            placeholder="e.g. Small, Red, Large & Blue, etc."
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label htmlFor="name">{formData.is_variant ? "Parent Name" : "Name"} <span className="text-red-500">*</span></Label>
                        <Input
                          id="name"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          className={cn("h-12", formData.is_variant && "bg-slate-50 text-slate-500 cursor-not-allowed")}
                          data-testid="product-name-input"
                          placeholder="Product Name"
                          disabled={formData.is_variant}
                        />
                      </div>
                    </div>
                    <div className={cn("grid gap-4", !formData.is_variant ? "grid-cols-1" : "grid-cols-1")}>
                      <div className="space-y-2">
                        <Label htmlFor="sku">SKU</Label>
                        <Input
                          id="sku"
                          value={formData.sku}
                          onChange={(e) => setFormData(prev => ({ ...prev, sku: e.target.value }))}
                          className="h-12"
                          data-testid="product-sku-input"
                        />
                      </div>
                    </div>

                    {!formData.is_variant && (
                      <div className="space-y-2">
                        <Label htmlFor="description">Description</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          rows={4}
                          data-testid="product-description-input"
                        />
                      </div>
                    )}

                    {!formData.is_variant && (
                      <div className="space-y-2">
                        <Label>Collections</Label>
                        <MultiSelect
                          options={availableCollections}
                          selected={formData.collections || []}
                          onChange={(selected) => setFormData(prev => ({ ...prev, collections: selected }))}
                          placeholder="Select collections..."
                        />
                        <p className="text-xs text-slate-500">Assign this product to one or more collections.</p>
                      </div>
                    )}

                    {!formData.is_variant && (
                      <div className="space-y-2">
                        <Label>Available In <span className="text-red-500">*</span></Label>
                        <MultiSelect
                          options={channelOptions}
                          selected={formData.channels || []}
                          onChange={(selected) => setFormData(prev => ({ ...prev, channels: selected }))}
                          placeholder="Select channels..."
                        />
                        <p className="text-xs text-slate-500">
                          Which storefronts sell this product. Set explicitly here, this wins over the
                          product's collections and survives them being re-tagged.
                        </p>
                      </div>
                    )}

                    {/* Add-on & Status Logic */}
                    <div className="space-y-4 pt-4 border-t mt-4">
                      <div className="flex items-center space-x-6">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_active"
                            checked={formData.is_active}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                          />
                          <Label htmlFor="is_active" className="cursor-pointer">Visible in Store</Label>
                        </div>

                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_available"
                            checked={formData.is_available}
                            onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_available: checked }))}
                          />
                          <Label htmlFor="is_available" className="cursor-pointer">Available (Purchasable)</Label>
                        </div>
                      </div>

                      {!formData.is_variant && (
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            id="is_addon"
                            checked={formData.is_addon}
                            onCheckedChange={(checked) => setFormData(prev => ({
                              ...prev,
                              is_addon: checked,
                              addons: checked ? [] : prev.addons
                            }))}
                          />
                          <Label htmlFor="is_addon" className="cursor-pointer">Mark as Add On Product</Label>
                        </div>
                      )}

                      {!formData.is_addon && !formData.is_variant && (
                        <div className="space-y-2">
                          <Label>Add On(s)</Label>
                          <MultiSelect
                            options={availableAddons}
                            selected={formData.addons || []}
                            onChange={(selected) => setFormData(prev => ({ ...prev, addons: selected }))}
                            placeholder="Select add-on products..."
                          />
                          <p className="text-xs text-slate-500">Products that can be added to this item.</p>
                        </div>
                      )}
                    </div>

                  </TabsContent>

                  <TabsContent value="pricing" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="base_price">Base Price ($) <span className="text-red-500">*</span></Label>
                        <Input
                          id="base_price"
                          type="number"
                          step="0.01"
                          required
                          value={formData.base_price}
                          onChange={(e) => setFormData(prev => ({ ...prev, base_price: e.target.value }))}
                          className="h-12 border-indigo-100"
                          data-testid="product-price-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="inventory">Inventory</Label>
                        <Input
                          id="inventory"
                          type="number"
                          value={formData.inventory_quantity}
                          onChange={(e) => setFormData(prev => ({ ...prev, inventory_quantity: e.target.value }))}
                          className="h-12"
                          data-testid="product-inventory-input"
                        />
                      </div>
                    </div>

                    <div className="space-y-2 pt-2">
                      <Label className="flex items-center gap-2">
                        Overridden Price ($)
                        <Badge variant="outline" className="text-[10px] font-normal border-amber-200 text-amber-700 bg-amber-50">Admin Only</Badge>
                      </Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label htmlFor="overridden_price" className="text-xs text-slate-600">e-Displayroom</Label>
                          <Input
                            id="overridden_price"
                            type="number"
                            step="0.01"
                            value={formData.overridden_price}
                            onChange={(e) => setFormData(prev => ({ ...prev, overridden_price: e.target.value }))}
                            className="h-12 border-amber-200 focus-visible:ring-amber-500"
                            placeholder="e.g. 99.99"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label htmlFor="infinity_overridden_price" className="text-xs text-blue-700">Infinity Touch</Label>
                          <Input
                            id="infinity_overridden_price"
                            type="number"
                            step="0.01"
                            value={formData.infinity_overridden_price}
                            onChange={(e) => setFormData(prev => ({ ...prev, infinity_overridden_price: e.target.value }))}
                            className="h-12 border-blue-200 focus-visible:ring-blue-500"
                            placeholder="e.g. 99.99"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-slate-500 flex items-start gap-1">
                        <Info className="w-3 h-3 mt-0.5 shrink-0" />
                        If set, this price overrides the channel's computed price (bypassing multipliers). Leave 0 or empty to use multipliers.
                      </p>
                    </div>
                  </TabsContent>

                  <TabsContent value="images" className="space-y-4 mt-4">
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-4 text-center hover:bg-slate-50 transition-colors cursor-pointer relative">
                      <input
                        type="file"
                        multiple
                        accept="image/*"
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                        onChange={async (e) => {
                          const files = Array.from(e.target.files);
                          if (files.length === 0) return;

                          const formData = new FormData();
                          files.forEach(file => formData.append('images', file));

                          try {
                            const res = await productsService.uploadImages(formData);
                            const newImageObjects = res.urls.map(url => ({ url })); // Map to object structure
                            setFormData(prev => ({
                              ...prev,
                              images: [...prev.images, ...newImageObjects],
                              image_url: prev.image_url || res.urls[0] // Set main image if empty
                            }));
                            toast.success("Images uploaded!");
                          } catch (err) {
                            toast.error("Upload failed");
                          }
                        }}
                      />
                      <div className="flex flex-col items-center gap-2 text-slate-500">
                        <Upload className="w-8 h-8" />
                        <p className="text-sm font-medium">Click or drag images here</p>
                      </div>
                    </div>

                    {/* Image Previews */}
                    {formData.images && formData.images.length > 0 && (
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {formData.images.map((imgObj, idx) => (
                          <div key={idx} className={`relative group aspect-square rounded-lg overflow-hidden border-2 ${formData.image_url === imgObj.url ? 'border-indigo-600 ring-2 ring-indigo-100' : 'border-slate-200'}`}>
                            <img src={getImageUrl(imgObj.url)} alt="Product" className="w-full h-full object-cover" />

                            {/* Cover Indicator / Set Cover Button */}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({ ...prev, image_url: imgObj.url }))}
                              className={`absolute top-1 left-1 p-1 rounded-full transition-all ${formData.image_url === imgObj.url ? 'bg-indigo-600 text-white opacity-100' : 'bg-white/90 text-slate-400 opacity-0 group-hover:opacity-100 hover:text-indigo-600'}`}
                              title={formData.image_url === imgObj.url ? "Cover Image" : "Set as Cover"}
                            >
                              <Star className={`w-3 h-3 ${formData.image_url === imgObj.url ? 'fill-current' : ''}`} />
                            </button>

                            {/* Delete Button */}
                            <button
                              type="button"
                              onClick={() => setFormData(prev => ({
                                ...prev,
                                images: prev.images.filter((_, i) => i !== idx),
                                image_url: prev.image_url === imgObj.url ? (prev.images.length > 1 ? prev.images.find((_, i) => i !== idx)?.url || "" : "") : prev.image_url
                              }))}
                              className="absolute top-1 right-1 bg-white/90 p-1 rounded-full text-red-500 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50"
                              title="Remove Image"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}


                  </TabsContent>

                  <TabsContent value="custom" className="space-y-4 mt-4">
                    <div className="space-y-2">
                      <Label>Mandatory Customization</Label>
                      <Select
                        value={formData.customizations?.mandatory?.[0] || "none"}
                        onValueChange={(val) => setFormData(prev => ({
                          ...prev,
                          customizations: {
                            ...prev.customizations,
                            mandatory: val !== "none" ? [val] : []
                          }
                        }))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select a mandatory customization..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {allCustomizations.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">A mandatory customization that customers MUST configure.</p>
                    </div>

                    <div className="space-y-2">
                      <Label>Optional Customization</Label>
                      <Select
                        value={formData.customizations?.optional?.[0] || "none"}
                        onValueChange={(val) => setFormData(prev => ({
                          ...prev,
                          customizations: {
                            ...prev.customizations,
                            optional: val !== "none" ? [val] : []
                          }
                        }))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select an optional customization..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {allCustomizations.map((c) => (
                            <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">An optional customization that customers CAN select if desired.</p>
                    </div>
                  </TabsContent>

                  {/* Variants Tab */}
                  {(editingProduct && !editingProduct.is_variant && !editingProduct.is_addon) && (
                    <TabsContent value="variants" className="space-y-4 mt-4">
                      <div className="flex justify-between items-center mb-4">
                        <Label className="text-base font-semibold">Product Variants</Label>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            // Open creation mode for a variant
                            setFormData({
                              name: editingProduct.name, // Keep exact parent name
                              description: editingProduct.description,
                              base_price: editingProduct.base_price,
                              category_id: editingProduct.category_id,
                              image_url: editingProduct.image_url,
                              images: editingProduct.images,
                              sku: editingProduct.sku + "-VAR", // Suggest a SKU
                              inventory_quantity: 0,
                              customizations: editingProduct.customizations,
                              is_addon: false,
                              addons: editingProduct.addons,
                              is_variant: true,
                              parent_id: editingProduct.product_id, // Link to parent
                              variant_name: "" // Reset for user to type
                            });
                            setParentProductBeforeVariant(editingProduct);
                            setEditingProduct(null); // Clear editing product to treat as new creation
                            // But we need a way to know we are creating a variant of specific parent if we cancel?
                            // Actually, standard "Add Product" flow but with pre-filled data is what we want.
                            // Wait, if we clear editingProduct, the Sheet title changes to "Add New Product".
                            // We might need a separate state or just rely on formData.parent_id to know it's a variant.
                            setParentProductName(editingProduct.name);
                            setActiveTab("general");
                          }}
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create New Variant
                        </Button>
                      </div>

                      <div className="border rounded-md">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Variant Name</TableHead>
                              <TableHead>SKU</TableHead>
                              <TableHead>Price</TableHead>
                              <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {editingProductVariants.length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center py-4 text-slate-500">
                                  No variants found.
                                </TableCell>
                              </TableRow>
                            ) : (
                              editingProductVariants.map(variant => (
                                <TableRow key={variant.product_id}>
                                  <TableCell>
                                    <div className="font-medium text-slate-900">{variant.variant_name || 'N/A'}</div>
                                    <div className="text-xs text-slate-500">{variant.name}</div>
                                  </TableCell>
                                  <TableCell>{variant.sku}</TableCell>
                                  <TableCell>${variant.base_price}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="flex justify-end gap-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setParentProductBeforeVariant(editingProduct);
                                          handleEdit(variant);
                                        }}
                                      >
                                        <Pencil className="w-4 h-4" />
                                      </Button>
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleDelete(variant.product_id, true, editingProduct.product_id)}
                                        className="text-red-600"
                                      >
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </TabsContent>
                  )}
                </Tabs>

                <div className="pt-4 mt-4 border-t sticky bottom-0 bg-white">
                  <Button
                    type="submit"
                    className="w-full bg-indigo-600 hover:bg-indigo-700 rounded-xl h-12"
                    data-testid="save-product-btn"
                  >
                    {editingProduct
                      ? "Update Product"
                      : formData.is_variant
                        ? "Create Variant"
                        : "Create Product"}
                  </Button>
                </div>
              </form>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      <div className="card-elevated overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-semibold">Product</TableHead>
              <TableHead className="font-semibold">Collections</TableHead>
              <TableHead className="font-semibold">Price</TableHead>
              <TableHead className="font-semibold">Inventory</TableHead>
              <TableHead className="font-semibold">Source</TableHead>
              <TableHead className="font-semibold">Channel</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-24">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-slate-500 font-medium animate-pulse">Filtering products...</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : filteredProducts.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                  No products found. Add a product or sync from Shopify.
                </TableCell>
              </TableRow>
            ) : (
              filteredProducts.map((product) => (
                <TableRow 
                  key={product.product_id} 
                  className={cn("table-row transition-opacity", !product.is_active && "opacity-60 grayscale-[0.3]")}
                >
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img
                          src={getImageUrl(product.image_url)}
                          alt={product.name}
                          className="w-12 h-12 rounded-lg object-contain bg-slate-50 border border-slate-100 p-0.5"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-slate-400" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          {product.is_addon && (
                            <Badge variant="outline" className="text-[10px] text-indigo-600 border-indigo-200 bg-indigo-50">Add On</Badge>
                          )}
                          {!product.is_active && (
                            <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50">Hidden</Badge>
                          )}
                        </div>
                        <p className={cn("font-medium", product.is_active ? "text-slate-900" : "text-slate-500")}>
                          {product.name}
                        </p>
                        {product.sku && (
                          <p className="text-xs text-slate-500 font-mono">{product.sku}</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {getCollectionNames(product.collections)}
                  </TableCell>
                  <TableCell className="font-medium">${product.base_price.toFixed(2)}</TableCell>
                  <TableCell>{product.inventory_quantity}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      {product.source === 'custom' ? (
                        <>
                          <Badge variant="outline" className="w-fit border-amber-200 bg-amber-50 text-amber-700">Reseller</Badge>
                          {product.store_name && <span className="text-[10px] text-amber-500 font-medium truncate max-w-[100px]" title={product.store_name}>{product.store_name}</span>}
                        </>
                      ) : (
                        <Badge variant="outline" className="w-fit border-indigo-200 bg-indigo-50 text-indigo-700">Admin</Badge>
                      )}
                      {product.shopify_product_id && (
                        <Badge variant="outline" className="w-fit border-green-200 bg-green-50 text-green-700 text-[10px]">Shopify Sync</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col items-start gap-1">
                      <div className="flex flex-wrap gap-1">
                        {(product.effective_channels || []).length === 0 ? (
                          <span className="text-xs text-slate-400 italic">—</span>
                        ) : (
                          (product.effective_channels || []).map((c) => (
                            <Badge key={c} variant="outline" className={cn("w-fit text-[10px]", CHANNEL_COLOR[c] || "border-slate-200 bg-slate-50 text-slate-600")}>
                              {CHANNEL_LABEL[c] || c}
                            </Badge>
                          ))
                        )}
                      </div>
                      {enableChannelColumn && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-6 px-2 text-[11px]"
                          onClick={() => setChannelDialogProduct(product)}
                          data-testid={`channels-btn-${product.product_id}`}
                        >
                          <Sliders className="w-3 h-3 mr-1" /> Channels
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(product)}
                        data-testid={`edit-product-${product.product_id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(product.product_id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`delete-product-${product.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {totalPages > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between px-4 py-4 border-t gap-4">
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <div>
              Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to <span className="font-medium">{Math.min(page * limit, totalItems)}</span> of <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex items-center gap-2">
              <span className="text-slate-600">Rows per page:</span>
              <Select
                value={limit.toString()}
                onValueChange={(val) => {
                  setLimit(Number(val));
                  setPage(1); // Reset to first page when changing page size
                }}
              >
                <SelectTrigger className="h-8 w-[70px]">
                  <SelectValue placeholder={limit.toString()} />
                </SelectTrigger>
                <SelectContent side="top">
                  {[10, 15, 25, 50, 100].map((pageSize) => (
                    <SelectItem key={pageSize} value={pageSize.toString()}>
                      {pageSize}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>

            {/* Numbered Pagination Logic */}
            <div className="flex items-center mx-2 space-x-1">
              {Array.from({ length: totalPages }).map((_, i) => {
                const pageNum = i + 1;
                // Show current page, first page, last page, and adjacent pages
                if (
                  pageNum === 1 ||
                  pageNum === totalPages ||
                  (pageNum >= page - 1 && pageNum <= page + 1)
                ) {
                  return (
                    <Button
                      key={pageNum}
                      variant={page === pageNum ? "default" : "outline"}
                      size="sm"
                      className={cn(
                        "h-8 w-8 p-0",
                        page === pageNum ? "bg-primary hover:bg-primary/90 text-white" : "text-slate-600 hover:bg-slate-100"
                      )}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                }

                // Ellipses logic
                if (pageNum === page - 2 || pageNum === page + 2) {
                  return <span key={pageNum} className="text-slate-400 px-1">...</span>;
                }

                return null;
              })}
            </div>

            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {enableChannelColumn && (
        <ChannelOverrideDialog
          product={channelDialogProduct}
          open={!!channelDialogProduct}
          onClose={() => setChannelDialogProduct(null)}
          onSaved={() => fetchData()}
        />
      )}
    </div>
  );
}
